import {AddSchema, MGLClass} from "mobx-graphlink";
import {Assert} from "js-vextensions";
import {O} from "web-vcore";
import {SessionEvent} from "../../../Engine/LiveSession/SessionEvent.js";
import {EngineConfig} from "../engineConfigs/@EngineConfig.js";
import {LogEntry} from "../../../UI/Tools/@Shared/LogEntry.js";
import {LaunchType} from "../engineConfigs/@EngineConfig/@EC_ModeSwitcher.js";
import {Setting, SettingValue} from "../engineConfigs/@EngineConfig/@Shared/Setting.js";
import _, {create} from "lodash";

export const SessionCoreData_propsWithSpecialRestore = ["events", "journey_alarmDelay"] as const;
/** Core session-data, segmented into a separate struct; if session crashes, this data gets restored during the session restart. */
export class SessionCoreData {
	// fields that are simply restored directly (no restore processing needed; albeit some may be [redundantly] set again during restore process)
	// ==========

	/** Temporary ID assigned to this session, while it is live/progressing. (a different ID gets assigned when the session is finished, and gets uploaded) */
	@O accessor id_live: string; // id of the session, while it was live
	@O accessor startTime: number;
	@O accessor launchType: LaunchType;
	@O accessor variantName: string|n;
	@O accessor settingValuesSelected: {[key: string]: SettingValue<any>} = {};
	@O accessor alarms_currentSequence: string|n;
	@O accessor alarms_sleepEnderActivatableFrom: number|n;
	@O accessor realityCheck_wakerWarnFrom: number|n;
	@O accessor realityCheck_wakerEffectFrom: number|n;
	@O accessor alarms_tillAlarmEnd_blockLightEffects: boolean|n;
	@O accessor alarms_tillAlarmEnd_blockMicroSnooze: boolean|n;
	@O accessor alarms_tillAlarmEnd_endAlarmAfterXMinutesOfNoAwakeSignal: number|n;

	// fields that need special processing at time of restore
	// ==========

	@O accessor events = [] as SessionEvent[]; // todo: make this the only place events are stored (right now it's just a copy of LiveSession.events)
	@O accessor journey_alarmDelay: number; // in ms
}

// session data that is stored permanently
@MGLClass({table: "sessions"})
export class Session {
	constructor(initialData?: Partial<Session>) { Object.assign(this, initialData); }

	//id: string;
	/** Whether session has locally-saved entry. */
	_local?: boolean;
	/** If session has locally-saved entry, this is the folder name the data is stored under. */
	_folderName?: string; // note: in-progress sessions have the equivalent "LiveSession.folderName"
	/** Whether session has web-saved entry. */
	_online?: boolean;

	id: string;
	creator: string;
	createdAt: number;
	configID: string;
	config_fieldOverrides_origValues: {[fieldPath: string]: any} = {};
	config: EngineConfig;

	// from SessionCoreData
	// todo: probably replace these individual fields with the full SessionCoreData struct, in the future (delayed, since requires db mutations)
	startTime: number;
	launchType: LaunchType;
	variantName: string|n;
	settingValuesSelected: {[key: string]: any};
	/** Only set if the alarm-sequence is locked to a single value in the config. */
	alarms_mainSequence: string|n;
	events: SessionEvent[];
	/** Only set if the alarm-delay is locked to a single value in the config. */
	alarmDelay?: number; // in ms

	/** How many minutes you'd need to subtract from the local-time (of the session-creator), for it to match the utc-time. (when session started) */
	localOffsetFromUTC: number;
	endTime: number;
	//week: string; // eg. "2020-08-02" (sunday where week started)
	week: number; // time at start of utc-week (in which session started)

	logEntries: LogEntry[];
}
AddSchema("Session", {
	properties: {
		id: {type: "string"},
		creator: {type: "string"},
		createdAt: {type: "number"},
		configID: {type: "string"},
		config: {$ref: "EngineConfig"},

		// from SessionCoreData
		startTime: {type: "number"},
		launchType: {$ref: "LaunchType"},
		variantName: {type: ["null", "string"]},
		settingValuesSelected: {patternProperties: {"^[a-zA-Z0-9_.]+$": {}}},
		alarms_mainSequence: {type: ["null", "string"]},
		events: {items: {$ref: "SessionEvent"}},
		alarmDelay: {type: ["null", "number"]},

		localOffsetFromUTC: {type: "number"},
		endTime: {type: "number"},
		week: {type: "number"},

		logEntries: {items: {$ref: "LogEntry"}},
	},
});

export function GetSettingValueFromSession<T>(session: Session, settingGetter: (config: EngineConfig)=>Setting<T>, defaultValue: T): T {
	const settingName = GetSettingNameFromGetter(settingGetter);
	return session.settingValuesSelected && settingName in session.settingValuesSelected
		? session.settingValuesSelected[settingName]
		: defaultValue;
}
export function GetSettingNameFromGetter<T>(settingGetter: (config: EngineConfig)=>Setting<T>) {
	const settingName = settingGetter.toString().match(/\.([a-zA-Z0-9_.]+)/)?.[1];
	Assert(settingName, `Failed to extract setting-name from settingGetter.toString(): ${settingGetter.toString()}`);
	return settingName;
}